"use client";

import * as React from "react";
import Image from "next/image";
import Link from "next/link";
import { Icon } from "@/components/ui/icon";

import { Container } from "./container";
import { Logo } from "./logo";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetClose,
} from "@/components/ui/sheet";
import { primaryNav, primaryCta } from "@/lib/content/nav";
import { trackEvent } from "@/lib/analytics/dataLayer";
import { cn } from "@/lib/utils";

const WELCOME_SEEN_KEY = "clarus-welcome-seen";

/**
 * One-time welcome speech bubble shown under the accessibility widget button
 * on a visitor's very first page load. After it appears once it sets a
 * localStorage flag so it never shows again. Renders nothing on the server and
 * on the first client render (both `shouldRender = false`) to avoid hydration
 * mismatch — the effect flips it on only when the flag is absent.
 */
function WidgetWelcomeBubble() {
  const [shouldRender, setShouldRender] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    let seen = false;
    try {
      seen = localStorage.getItem(WELCOME_SEEN_KEY) === "1";
    } catch {
      // localStorage unavailable (private mode etc.) — just skip the greeting.
      return;
    }
    if (seen) return;
    setShouldRender(true);
    // Small delay so the bubble feels like a deliberate greeting rather than a
    // flash on load. Mark as seen only when it actually opens — setting the
    // flag earlier would make StrictMode's double-invoked effect (dev) bail on
    // its second run after the first run's cleanup cancelled the timer.
    const showTimer = setTimeout(() => {
      setOpen(true);
      try {
        localStorage.setItem(WELCOME_SEEN_KEY, "1");
      } catch {
        /* ignore */
      }
    }, 1100);
    return () => clearTimeout(showTimer);
  }, []);

  // Auto-dismiss after a comfortable reading window.
  React.useEffect(() => {
    if (!open) return;
    const hideTimer = setTimeout(() => setOpen(false), 11000);
    return () => clearTimeout(hideTimer);
  }, [open]);

  if (!shouldRender) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "absolute right-0 top-full z-50 mt-3 w-64 rounded-card border-[1.5px] border-clarus-dark bg-white p-4 shadow-[4px_4px_0_0_var(--color-clarus-dark)] transition-[opacity,transform] duration-300 ease-[ease]",
        open
          ? "translate-y-0 opacity-100"
          : "pointer-events-none -translate-y-1 opacity-0",
      )}
    >
      {/* Tail — a rotated square sitting on the top edge, centred under the
          44px button (button centre ≈ 22px from the right). */}
      <span
        aria-hidden="true"
        className="absolute -top-[7px] right-4 h-3 w-3 rotate-45 border-l-[1.5px] border-t-[1.5px] border-clarus-dark bg-white"
      />
      <button
        type="button"
        onClick={() => setOpen(false)}
        aria-label="Hinweis schließen"
        className="absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-full text-clarus-dark/50 transition-colors hover:text-clarus-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clarus-purple"
      >
        <Icon name="close" className="text-[14px]" />
      </button>
      <p
        className="pr-5 text-clarus-dark"
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 800,
          fontSize: "0.95rem",
        }}
      >
        Willkommen bei ClarusPlus 👋
      </p>
      <p
        className="mt-1.5 text-clarus-dark/80"
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "0.8125rem",
          lineHeight: 1.5,
        }}
      >
        Schön, dass Sie da sind! Über diesen Button öffnen Sie jederzeit unser
        Barrierefreiheits-Widget.
      </p>
    </div>
  );
}

const mobileNav = primaryNav.filter((item) => item.label !== "Login");

export function Header() {
  return (
    <header className="sticky top-0 z-40 border-b-2 border-clarus-dark bg-background">
      <Container className="flex h-20 items-center justify-between gap-6">
        {/* Logo is a plain home link on every breakpoint (no dropdown menu on
            mobile — navigation lives in the ☰ sheet). */}
        <Logo />

        <div className="hidden items-center gap-8 lg:flex">
          <nav
            aria-label="Hauptnavigation"
            className="flex items-center gap-8"
          >
            {primaryNav.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="text-annotation font-bold text-clarus-dark/50 transition-colors hover:text-clarus-dark"
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Button
              asChild
              size="md"
              variant="outline"
            >
              <Link
                href={primaryCta.href}
                onClick={() => trackEvent("cta_register", { location: "header" })}
                className="gap-2"
              >
                {primaryCta.label}
                <Icon name="arrow_forward" className="text-[16px]" />
              </Link>
            </Button>
            <div className="relative flex items-center">
              <button
                type="button"
                data-clarus-trigger
                aria-label="Clarus — Barrierefreiheits-Widget öffnen"
                title="Clarus — Barrierefreiheits-Widget"
                className="relative inline-flex h-11 w-11 items-center justify-center overflow-hidden rounded-full bg-clarus-dark transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clarus-purple focus-visible:ring-offset-2"
              >
                {/* Animated accessibility mascot. The GIF is a 16:9 frame whose
                    own dark background matches `bg-clarus-dark` and already
                    contains the figure inside a circle; `object-cover` + a slight
                    zoom crop that circle flush to the round button edge so only
                    the animated figure shows (no double ring). */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="/illustrations/stickman_bfsg.gif"
                  alt=""
                  aria-hidden="true"
                  className="h-full w-full scale-[1.3] object-cover"
                />
              </button>
              <WidgetWelcomeBubble />
            </div>
          </div>
        </div>

        <Sheet>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              aria-label="Menü öffnen"
            >
              <Icon name="menu" className="ms-thick text-[20px]" />
            </Button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="left-0 right-0 top-0 bottom-0 h-[100dvh] w-[100dvw] max-w-none border-none bg-clarus-dark p-6 text-clarus-yellow shadow-none sm:max-w-none"
          >
            <SheetTitle className="sr-only">Menü</SheetTitle>
            <div className="flex h-full flex-col items-center justify-center gap-12">
              <Image
                src="/brand/logo-yellow.png"
                alt=""
                width={1518}
                height={1486}
                priority
                sizes="44px"
                style={{ width: 44, height: "auto" }}
                className="select-none"
              />

              <nav
                aria-label="Mobile Navigation"
                className="flex flex-col items-center gap-8"
              >
                {mobileNav.map((item) => (
                  <SheetClose asChild key={item.href}>
                    <Link
                      href={item.href}
                      className="text-display-md font-bold text-clarus-yellow"
                    >
                      {item.label}
                    </Link>
                  </SheetClose>
                ))}
                <SheetClose asChild>
                  <Link
                    href={primaryCta.href}
                    className="text-display-md font-bold text-clarus-yellow"
                  >
                    {primaryCta.label}
                  </Link>
                </SheetClose>
              </nav>

              {/* Wrapped in SheetClose: tapping the widget trigger first CLOSES
                  this fullscreen menu so Radix's scroll-lock + focus-trap
                  (react-remove-scroll) is released BEFORE the Clarus widget
                  opens. Otherwise the widget panel renders outside the still-open
                  modal, where react-remove-scroll blocks all touch-scrolling on
                  iOS Safari — the page (and the widget) freeze. */}
              <SheetClose asChild>
                <button
                  type="button"
                  data-clarus-trigger
                  aria-label="Clarus — Barrierefreiheits-Widget öffnen"
                  title="Clarus — Barrierefreiheits-Widget"
                  className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-clarus-yellow transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clarus-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-clarus-dark"
                >
                  <span
                    aria-hidden="true"
                    className="h-9 w-9 bg-clarus-dark [mask-image:url('/illustrations/stickman.png')] [mask-size:contain] [mask-position:center] [mask-repeat:no-repeat]"
                  />
                </button>
              </SheetClose>
            </div>
          </SheetContent>
        </Sheet>
      </Container>
    </header>
  );
}
